<template>
  <page-outline 
    :title="$t('common.bic.bic_onus')"
    :show-filter="true"
    :show-refresh="true"
    :filter-expanded="displayFilter"
    :refresh-disabled="activateSpinner"
    @toggle-filter="toggleFilter"
    @refresh="onRefresh">
    <template v-slot:primaryActions>
      <app-button
        :text="$t('common.bic.bic_onus_add')"
        icon="fa-light fa-plus"
        variant="primary"
        @click="actionAdd"
      />
    </template>
    
    <template slot="filterContent">
      <app-panel
        :filters="filterConfig"
        :searchAriaLabel="$t('common.srch') + ' ' + $t('common.bic.bic_onus')"
        @filter-changed="onFilterChanged"
        @clear-filters="clearFilterFields"
        @apply-filters="onRefresh"
      />
    </template>
    
    <template v-slot:pageContent>
      <div class="listPage">
        <section aria-labelledby="results-section-title" class="mt-4">
          <h2 id="results-section-title" class="visually-hidden">{{ $t('common.results') }}</h2>
          <b-row>
            <b-col cols="12">
              <div class="table-responsive">
                <spinner v-if="activateSpinner"></spinner>
              <b-table v-else-if="listItemsBICOnus.length > 0"
                       :items="listItemsBICOnus"
                       :fields="fields"
                       class="table-striped table-hover"
                       :selectable="listItemsBICOnus.length > 0"
                       :empty-text="$t('common.security.no_result')"
                       style="margin-bottom:0"
                       :sort-by.sync="sortBy"
                       :sort-desc.sync="sortDir"
                       @head-clicked="sortTable"
                       @row-selected="onRowClicked"
                       :key="refreshKey"
                       :aria-label="$t('common.bic.bic_onus') + ' - ' + $t('common.table')"
                       role="table">
                <template #cell(edit)="data">
                  <b-button 
                    variant="link" 
                    class="text-center d-inline-block py-3 w-100 p-0 table-action-btn"
                    :aria-label="$t('rights.edit') + ' ' + $t('common.global.bic') + ' ' + data.item.BIC"
                    @click="actionEdit($event, data.item.IdBICOnus)">
                    <i class="fa-regular fa-pencil" aria-hidden="true"></i>
                  </b-button>
                </template>
                <template #cell(delete)="data">
                  <b-button 
                    variant="link" 
                    class="text-center d-inline-block py-3 w-100 p-0 table-action-btn table-action-btn-danger"
                    :aria-label="$t('rights.delete') + ' ' + $t('common.global.bic') + ' ' + data.item.BIC"
                    @click="actionDelete($event, data.item.IdBICOnus)">
                    <i class="fa-regular fa-trash" aria-hidden="true"></i>
                  </b-button>
                </template>
              </b-table>
              <div v-else class="d-flex justify-content-center">
                <span class="text-muted fs-5">
                  {{ $t('common.no_result') }}
                </span>
              </div>
            </div>
          </b-col>
        </b-row>
        </section>
        <nav :aria-label="$t('common.pagination')">
          <b-row class="mt-5">
            <b-col>
              <cm-pagination
                  :maxResultsByPage="paginationObject.maxResults"
                  :firstIndex="paginationObject.firstIndex"
                  :nbResultsTotal="paginationObject.nbResultsTotal"
                  :refreshEvent="paginationObject.refresh">
              </cm-pagination>
            </b-col>
          </b-row>
        </nav>
      </div>
    </template>
  </page-outline>
</template>

<script>
import formWriteGroupLabelInput from "@/components/common/formWriteGroupLabelInput.vue"
import Pagination from '@/components/common/pagination.vue'
import moment from 'moment-timezone'
import PageOutline from "@/components/common/PageOutline.vue"
import Spinner from "@/components/common/spinner.vue"
import AppButton from "@/components/common/AppButton.vue"
import AppPanel from "@/components/common/AppPanel.vue"
import { errorMessageAlert } from "@/helpers/alert"
import { loadPage } from "@/helpers/routing"

export default {
  name: "BICOnusList",
  components: {
    'cm-pagination': Pagination,
    "form-write-group-label-input": formWriteGroupLabelInput,
    "page-outline": PageOutline,
    spinner: Spinner,
    AppButton,
    AppPanel
  },
  props: {
    id: {
      type: String,
      default: "-1",
    },
  },
  data() {
    return {
      activateSpinner: false,
      displayFilter: false,
      filterConditions: {},
      listItemsBICOnus: [],
      BIC: '',
      pageCounter: 0,
      paginationObject: {},
      refreshKey: false,
      sortBy: '',
      sortDir: false,
      timezone: '+00:00',
      filterValues: {
        BIC: ''
      }
    }
  },
  created() {
    this.timezone = moment.tz.guess()
    if (this.filterConditions) {
      if (this.filterConditions.OrderByField) {
        this.sortBy = this.filterConditions.OrderByField
      }
      if (this.filterConditions.OrderByDirection) {
        this.sortDir = this.filterConditions.OrderByDirection === 'DESC'
      }
    }

    this.activateSpinner = true

    this.paginationObject['firstIndex'] = 1
    this.paginationObject['maxResults'] = 15
    this.paginationObject['nbResultsTotal'] = 0
    let that = this
    this.$bus.$off('refresh-cache-contacts')

    this.paginationObject['refresh'] = 'direct-inbox-filtered'
    this.$bus.$off('direct-inbox-filtered')
    this.$bus.$on('direct-inbox-filtered', index => {
      this.getBICOnus(index)
      that.pageCounter = 0
    })

    this.$f7.initPullToRefresh()
    this.pageCounter = 0

    this.getBICOnus()

    if (this.id !== "-1") {
      this.tabName = parseInt(this.id)
    }
  },
  computed: {
    filterConfig() {
      return [
        {
          id: 'filter-bic',
          type: 'text',
          prop: 'BIC',
          labelKey: 'annuaire.bic',
          icon: 'fa-solid fa-building-columns',
          value: this.filterValues.BIC,
          placeholder: this.$t('common.global.bic'),
          colSm: '12',
          colLg: '12'
        }
      ]
    },
    canDelete () {
      return true
    },
    canEdit () {
      return true
    },
    fields () {
      let fields = []
      fields.push({
        key: 'BIC',
        label: this.$t('common.global.bic'),
        thClass: 'text-center table-condensed-ipad',
        tdClass: 'p-0 text-center w-100 p-2 py-3',
        sortable: true
      })
      if (this.canEdit) {
        fields.push({ key: 'edit', label: this.$t('rights.edit'), thClass: 'text-center table-condensed-ipad w-10', tdClass: 'p-0', sortable: false })
      }
      if (this.canDelete) {
        fields.push({ key: 'delete', label: this.$t('rights.delete'), thClass: 'text-center table-condensed-ipad w-10', tdClass: 'p-0', sortable: false })
      }
      return fields
    }
  },
  methods: {
    actionAdd () {
      loadPage(this, {url: '/biconus/add/-1'})
    },
    actionDelete ($e, id) {
      $e.stopPropagation()
      let that = this
      this.$f7.modal({
        title: this.$t('common.alerts.element_deletion_confirm'),
        buttons: [
          {
            text: this.$t('creation_forms.vldt'),
            onClick: function () {
              let params = { id: id }
              that.$engine.firstOn().bic.bic.deleteOnus(params)
                  .then(resp => {
                    that.listItemsBICOnus = that.listItemsBICOnus.filter(item => item.IdBICOnus !== id)
                    that.$f7.modal({
                      title: that.$t('common.bic.bic_onus_deleted'),
                      buttons: [
                        { text: 'OK', onClick: function () { return } }
                      ]
                    })
                    loadPage(that, { url: '/biconus/' })
                  })
                  .catch(e => {
                    errorMessageAlert(e, that, that.$i18n)
                    that.okMessageBox(that.$t('common.bic.bic_onus_delete_error'))
                  })
            }
          },
          {
            text: this.$t('common.cncl'),
            onClick: function () { return }
          }
        ]
      })
    },
    actionEdit ($e, id) {
      $e.stopPropagation()
      loadPage(this, { url: '/biconus/edit/' + id })
    },
    applyFilter(filterConditions = {}) {
      this.filterConditions = filterConditions

      this.pageCounter = 0
      this.paginationObject['firstIndex'] = 1
      this.paginationObject['nbResultsTotal'] = 0

      this.activateSpinner = true

      if (this.filterConditions.OrderByField) {
        this.sortBy = this.filterConditions.OrderByField
      }
      if (this.filterConditions.OrderByDirection) {
        this.sortDir = this.filterConditions.OrderByDirection === 'DESC'
      }

      this.getBICOnus()
    },
    clearFilterFields() {
      this.BIC = ''
      this.filterValues.BIC = ''
      this.applyFilter()
    },
    onFilterChanged({ prop, value }) {
      this.filterValues[prop] = value
      if (prop === 'BIC') {
        this.BIC = value
      }
    },
    toggleFilter() {
      this.displayFilter = !this.displayFilter
    },
    getBICOnus(index = 1) {
      this.pageCounter = 0
      this.paginationObject['firstIndex'] = index
      this.paginationObject['nbResultsTotal'] = 0
      let that = this
      let params = {
        IdBICOnus: this.IdBICOnus,
        BIC: this.BIC,
        maxResults: this.paginationObject.maxResults,
        firstIndex: this.paginationObject.firstIndex
      }
      if (this.filterConditions.OrderByField) {
        params.OrderByField = this.filterConditions.OrderByField
      }
      if (this.filterConditions.OrderByDirection) {
        params.OrderByDirection = this.filterConditions.OrderByDirection
      }
      this.$engine.firstOn().bic.bic.fetchOnus(params).then((resp) => {
        that.paginationObject.nbResultsTotal = resp.data.nbTotalResults
        that.paginationObject.nbResults = resp.data.BICOnus.length

        let BICOnus = {}

        BICOnus = resp.data.BICOnus

        that.updateBICOnus(BICOnus)

        this.$f7.pullToRefreshDone()
        that.activateSpinner = false
      })
      .catch(error => {
        errorMessageAlert(error, that)
      })
    },
    getDate (date) {
      if (!date || date == ''){
        return '-'
      }
      const hasTimezone = /([+-]\d{2}:\d{2}|Z)$/.test(date);
      const isoWithTZ = hasTimezone ? date : date + 'Z';
      return moment(isoWithTZ).tz(this.timezone).format('DD/MM/YYYY HH:mm:ss');
    },
    okMessageBox (message) {
      this.$f7.modal({
        title: message,
        buttons: [
          {
            text: 'OK',
            onClick: function () {
              return
            }
          }
        ]
      })
    },
    onRefresh() {
      this.refreshKey = !this.refreshKey
      this.activateSpinner = true
      this.getBICOnus()
    },
    onRowClicked(item) {
      if (item[0] && item[0].IdBICOnus) {
        loadPage(this, { url: '/biconus/details/' + item[0].IdBICOnus })
      }
    },
    sortTable (key, field, event, isFooter) {
      const sortableFields = [
        'BIC'
      ]

      if (!sortableFields.includes(key)) {
        return
      }

      let sortDirection = 'ASC'
      if (key === this.sortBy && this.sortDir === false) {
        sortDirection = 'DESC'
      }

      this.filterConditions.OrderByField = key
      this.filterConditions.OrderByDirection = sortDirection

      this.applyFilter(this.filterConditions, this.paginationObject.firstIndex)
    },
    updateBICOnus(BICOnus) {
      this.listItemsBICOnus = BICOnus
    }
  }
}
</script>

<style scoped>
/* Focus avec couleur DealPrudence */
.btn:focus,
.b-button:focus,
button:focus,
input:focus,
select:focus,
textarea:focus,
a:focus,
[tabindex]:focus {
  box-shadow: 0 0 0 0.25rem rgba(17, 176, 175, 0.25) !important;
}

/* Boutons d'actions du tableau avec couleurs DealPrudence */
.table-action-btn {
  color: #11B0AF !important;
  text-decoration: none !important;
  transition: all 0.3s ease;
}

.table-action-btn:hover {
  color: #0E9594 !important;
  background-color: rgba(17, 176, 175, 0.1) !important;
  text-decoration: none !important;
}

.table-action-btn:focus {
  color: #0E9594 !important;
  box-shadow: 0 0 0 0.25rem rgba(17, 176, 175, 0.25) !important;
  text-decoration: none !important;
}

/* Bouton supprimer avec couleur danger mais hover turquoise */
.table-action-btn-danger {
  color: #dc3545 !important;
}

.table-action-btn-danger:hover {
  color: #0E9594 !important;
  background-color: rgba(17, 176, 175, 0.1) !important;
}

.table-action-btn-danger:focus {
  color: #0E9594 !important;
}
</style>
